import { useState } from "react";
import { api, currentRole } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import RecruitmentSection from "../components/RecruitmentSection";

interface Leave {
  id: number; userId: number; userName: string; type: string;
  startDate: string; endDate: string; reason: string | null; status: string;
}
interface Ticket { id: number; category: string; subject: string; body: string; status: string }
interface Comment { id: number; body: string; authorName: string; createdAt: string }
interface TicketDetail extends Ticket { comments: Comment[] }

const field = "input";
const statusChip: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700", approved: "bg-teal-50 text-teal-700", rejected: "bg-red-100 text-red-700",
  open: "bg-amber-50 text-amber-700", in_progress: "bg-sky-50 text-sky-700",
  resolved: "bg-teal-50 text-teal-700", closed: "bg-slate-100 text-faint",
};
const LEAVE_TYPES = ["annual", "sick", "unpaid", "carers", "other"];
const TICKET_CATEGORIES = ["payroll", "leave", "policy", "technical", "performance", "incident", "other"];

export default function HR() {
  const manager = ["owner", "coordinator"].includes(currentRole() ?? "");
  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold text-ink">HR</h1>
      <LeaveSection manager={manager} />
      <TicketSection manager={manager} />
      {manager && <RecruitmentSection />}
    </div>
  );
}

function LeaveSection({ manager }: { manager: boolean }) {
  const { data, reload } = useFetch<Leave[]>("/v1/workforce/leave");
  const [form, setForm] = useState({ type: "annual", startDate: "", endDate: "", reason: "" });
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api("/v1/workforce/leave", { method: "POST", body: JSON.stringify(form) });
      setForm({ type: "annual", startDate: "", endDate: "", reason: "" }); reload();
    } catch (err) { setError((err as Error).message); }
  }
  async function decide(id: number, status: "approved" | "rejected") {
    await api(`/v1/workforce/leave/${id}/decision`, { method: "POST", body: JSON.stringify({ status }) });
    reload();
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-ink">Leave {manager && <span className="text-sm text-faint">(org-wide)</span>}</h2>
      <form onSubmit={submit} className="card p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
        <label className="block"><span className="label">Type</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={field}>
            {LEAVE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label className="block"><span className="label">From</span>
          <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className={field} required /></label>
        <label className="block"><span className="label">To</span>
          <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className={field} required /></label>
        <label className="block"><span className="label">Reason</span>
          <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={field} /></label>
        <button className="btn-dark">Request leave</button>
        {error && <p className="text-sm text-red-600 col-span-full">{error}</p>}
      </form>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-edge-soft">
            {manager && <th className="th">Worker</th>}
            <th className="th">Type</th><th className="th">Dates</th><th className="th">Status</th>{manager && <th className="th"></th>}
          </tr></thead>
          <tbody>
            {(data ?? []).map((l) => (
              <tr key={l.id} className="border-b border-edge-soft last:border-0">
                {manager && <td className="px-5 py-3 text-ink">{l.userName}</td>}
                <td className="px-5 py-3 capitalize">{l.type}</td>
                <td className="px-5 py-3 text-mut">{l.startDate} → {l.endDate}</td>
                <td className="px-5 py-3"><span className={`chip ${statusChip[l.status]}`}>{l.status}</span></td>
                {manager && <td className="px-5 py-3 text-right">
                  {l.status === "pending" && (
                    <span className="flex gap-3 justify-end">
                      <button onClick={() => decide(l.id, "approved")} className="text-teal-700 hover:underline">Approve</button>
                      <button onClick={() => decide(l.id, "rejected")} className="text-red-600 hover:underline">Reject</button>
                    </span>
                  )}
                </td>}
              </tr>
            ))}
            {(data ?? []).length === 0 && <tr><td className="px-5 py-6 text-mut" colSpan={manager ? 5 : 3}>No leave requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TicketSection({ manager }: { manager: boolean }) {
  const { data, reload } = useFetch<Ticket[]>("/v1/workforce/tickets");
  const [form, setForm] = useState({ category: "payroll", subject: "", body: "" });
  const [openId, setOpenId] = useState<number | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return;
    await api("/v1/workforce/tickets", { method: "POST", body: JSON.stringify(form) });
    setForm({ category: "payroll", subject: "", body: "" }); reload();
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-ink">HR tickets</h2>
      <form onSubmit={submit} className="card p-4 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
        <label className="block"><span className="label">Category</span>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
            {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></label>
        <label className="block sm:col-span-2"><span className="label">Subject</span>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={field} /></label>
        <label className="block sm:col-span-2"><span className="label">Details</span>
          <input value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={field} /></label>
        <button className="btn-dark">Raise ticket</button>
      </form>
      <div className="space-y-2">
        {(data ?? []).map((t) => (
          <div key={t.id} className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-ink">{t.subject} <span className="chip ml-1 bg-slate-100 text-body">{t.category}</span></p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`chip ${statusChip[t.status]}`}>{t.status.replace(/_/g, " ")}</span>
                <button onClick={() => setOpenId(openId === t.id ? null : t.id)} className="text-sm text-teal-700 hover:underline">
                  {openId === t.id ? "Close" : "Open"}
                </button>
              </div>
            </div>
            {openId === t.id && <TicketThread ticketId={t.id} manager={manager} onChange={reload} />}
          </div>
        ))}
        {(data ?? []).length === 0 && <p className="text-sm text-mut">No tickets.</p>}
      </div>
    </section>
  );
}

function TicketThread({ ticketId, manager, onChange }: { ticketId: number; manager: boolean; onChange: () => void }) {
  const detail = useFetch<TicketDetail>(`/v1/workforce/tickets/${ticketId}`);
  const [body, setBody] = useState("");

  async function comment() {
    if (!body.trim()) return;
    await api(`/v1/workforce/tickets/${ticketId}/comments`, { method: "POST", body: JSON.stringify({ body }) });
    setBody(""); detail.reload();
  }
  async function setStatus(status: string) {
    await api(`/v1/workforce/tickets/${ticketId}/status`, { method: "POST", body: JSON.stringify({ status }) });
    detail.reload(); onChange();
  }

  if (detail.loading || !detail.data) return <div className="skeleton h-16 mt-3" />;
  return (
    <div className="mt-3 border-t border-edge-soft pt-3 space-y-2">
      <p className="text-sm text-body">{detail.data.body}</p>
      {detail.data.comments.map((c) => (
        <div key={c.id} className="text-sm bg-hover rounded-lg px-3 py-2">
          <span className="font-medium text-ink">{c.authorName}: </span><span className="text-body">{c.body}</span>
        </div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment…" className="input mt-0 flex-1 text-sm" />
        <button onClick={comment} className="btn-secondary px-3 py-1.5 text-sm">Comment</button>
        {manager && detail.data.status !== "resolved" && (
          <button onClick={() => setStatus("resolved")} className="btn-dark text-sm">Resolve</button>
        )}
      </div>
    </div>
  );
}
